import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  useColorModeValue, 
  usePrefersReducedMotion,
  Tooltip,
  Portal
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoChevronUp } from 'react-icons/io5';
import { ANIMATION_CONSTANTS } from '../../constants/animations';

const BackToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Medical-grade color scheme
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const iconColor = useColorModeValue('gray.600', 'gray.300');
  const shadow = useColorModeValue(
    '0 4px 12px rgba(0, 0, 0, 0.1)',
    '0 4px 12px rgba(0, 0, 0, 0.3)'
  );

  // Detect scroll position
  useEffect(() => {
    const toggleVisibility = () => {
      // Show button after scrolling past hero section (approximately 100vh)
      const heroHeight = window.innerHeight;
      setIsVisible(window.pageYOffset > heroHeight);
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Smooth scroll to top
  const scrollToTop = () => {
    const duration = prefersReducedMotion ? 0 : ANIMATION_CONSTANTS.SCROLL.DURATION;
    
    if (prefersReducedMotion) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const start = window.pageYOffset;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Medical-grade easing: smooth, confident, no bounce
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      window.scrollTo(0, start * (1 - easeOutCubic));
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  // Animation variants for medical-grade motion
  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : ANIMATION_CONSTANTS.DURATIONS.NORMAL / 1000,
        ease: ANIMATION_CONSTANTS.EASING.PRIMARY,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: prefersReducedMotion ? 0 : ANIMATION_CONSTANTS.DURATIONS.FAST / 1000,
        ease: ANIMATION_CONSTANTS.EASING.PRIMARY,
      },
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: prefersReducedMotion ? 0 : ANIMATION_CONSTANTS.DURATIONS.FAST / 1000,
        ease: ANIMATION_CONSTANTS.EASING.PRIMARY,
      },
    },
  };

  return (
    <Portal>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={buttonVariants}
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              zIndex: 1000,
            }}
          >
            <Tooltip
              label="Return to top"
              placement="left"
              hasArrow
              bg={useColorModeValue('gray.800', 'gray.200')}
              color={useColorModeValue('white', 'gray.800')}
            >
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
              >
                <IconButton
                  aria-label="Return to top of page"
                  icon={<IoChevronUp size={20} />}
                  onClick={scrollToTop}
                  size="lg"
                  borderRadius="full"
                  bg={bgColor}
                  color={iconColor}
                  border="1px solid"
                  borderColor={borderColor}
                  _hover={{
                    bg: hoverBg,
                    transform: prefersReducedMotion ? 'none' : 'translateY(-2px)',
                  }}
                  _active={{
                    bg: hoverBg,
                  }}
                  boxShadow={shadow}
                  transition={prefersReducedMotion ? 'none' : 'all 0.2s ease-in-out'}
                  sx={{
                    '&:focus': {
                      boxShadow: `0 0 0 3px ${useColorModeValue('blue.200', 'blue.600')}`,
                    },
                  }}
                />
              </motion.div>
            </Tooltip>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default BackToTopButton; 